'use client';

import { useEffect, useId, useRef } from 'react';
import type { RejectionReason, ReviewCandidate } from '../lib/api';

export function rejectionReasonsFor(candidate: Pick<ReviewCandidate, 'card_type'>) {
  return [
    { key: '1', reason: 'incorrect', label: 'Incorrect or unsupported' },
    { key: '2', reason: 'low_yield', label: 'Low-yield or trivial' },
    { key: '3', reason: 'duplicate', label: 'Duplicate' },
    { key: '4', reason: 'wording', label: 'Poor wording' },
    candidate.card_type === 'image_occlusion'
      ? { key: '5', reason: 'image_mask', label: 'Bad image or mask' }
      : { key: '5', reason: 'other', label: 'Other issue' },
  ] as { key: string; reason: RejectionReason; label: string }[];
}

export function RejectionReasonDialog({ candidate, error, submitting, onChoose, onSkip }: {
  candidate: ReviewCandidate;
  error: string;
  submitting: boolean;
  onChoose: (reason: RejectionReason) => void;
  onSkip: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const version = candidate.current_version;
  const image = version.occluded_image_ref || version.image_ref;
  const prompt = candidate.card_type === 'cloze'
    ? (version.cloze_text || '').replace(/\{\{c\d+::([\s\S]*?)(?:::[\s\S]*?)?\}\}/g, '[$1]')
    : candidate.card_type === 'image_occlusion'
      ? version.hint || 'Which structure is hidden?'
      : version.front;
  const answer = candidate.card_type === 'basic' ? version.back : version.answer;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    // Native modality makes the background inert, traps focus, and restores it
    // on close. Focus the heading so Enter cannot accidentally select a reason.
    dialog.showModal();
    headingRef.current?.focus();
    return () => dialog.close();
  }, []);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      aria-modal="true"
      aria-busy={submitting}
      className="fixed inset-0 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-xl overflow-y-auto rounded-2xl border border-border bg-card p-5 text-foreground shadow-2xl backdrop:bg-black/60 sm:p-6"
      onCancel={event => { event.preventDefault(); if (!submitting) onSkip(); }}
      onKeyDown={event => {
        // Nothing typed in this popup should reach the next card's shortcuts.
        event.stopPropagation();
        if (submitting || event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
        const choice = rejectionReasonsFor(candidate).find(item => item.key === event.key);
        if (choice) { event.preventDefault(); onChoose(choice.reason); }
      }}
    >
      <h2 ref={headingRef} tabIndex={-1} id={titleId} className="font-serif text-2xl font-semibold outline-none">Why did you reject this card?</h2>
      <p id={descriptionId} className="mt-2 text-sm text-muted">Optional—choose a reason or skip to continue. This feedback applies only to the card below.</p>
      <div className="mt-4 max-h-56 overflow-y-auto rounded-xl border border-border bg-surface p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Rejected card</p>
        {image && <img src={image} alt="Rejected card image" className="mb-3 max-h-32 max-w-full rounded-lg object-contain" />}
        <p className="whitespace-pre-wrap text-sm font-medium leading-6">{prompt}</p>
        {answer && <p className="mt-2 whitespace-pre-wrap border-t border-border pt-2 text-sm leading-6 text-muted">{answer}</p>}
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {rejectionReasonsFor(candidate).map(choice => (
          <button key={choice.key} type="button" onClick={() => onChoose(choice.reason)} disabled={submitting}
            className="min-h-11 rounded-xl border border-border bg-surface px-3 py-3 text-left text-sm font-semibold transition-colors enabled:hover:border-primary enabled:hover:bg-primary-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-40">
            <span className="mr-2 font-mono text-primary">{choice.key}</span>{' '}{choice.label}
          </button>
        ))}
      </div>
      {submitting && <p className="mt-3 text-sm text-muted" role="status">Saving your feedback…</p>}
      {error && <p className="mt-3 text-sm text-danger" role="alert">{error}</p>}
      <div className="mt-4 flex justify-end">
        <button type="button" onClick={onSkip} disabled={submitting} className="ui-button ui-button--secondary">Skip · Esc</button>
      </div>
    </dialog>
  );
}
